import { defineStore } from 'pinia'

const TRANSLATION_LANGUAGES = {
	afrikánčina: 'af',
	albánčina: 'sq',
	amharčina: 'am',
	arabčina: 'ar',
	arménčina: 'hy',
	asámčina: 'as',
	ajmarčina: 'ay',
	azerbajdžančina: 'az',
	bambarčina: 'bm',
	baskičtina: 'eu',
	bieloruština: 'be',
	bengálčina: 'bn',
	bhojpurčina: 'bho',
	bosniančina: 'bs',
	bulharčina: 'bg',
	katalánčina: 'ca',
	cebuánčina: 'ceb',
	'čínština (zjednodušená)': 'zh-CN',
	'čínština (tradičná)': 'zh-TW',
	korzičtina: 'co',
	chorvátčina: 'hr',
	čeština: 'cs',
	dánčina: 'da',
	maldivčina: 'dv',
	dogri: 'doi',
	holandčina: 'nl',
	angličtina: 'en',
	esperanto: 'eo',
	estónčina: 'et',
	ewe: 'ee',
	'filipínčina (tagalčina)': 'fil',
	fínčina: 'fi',
	francúzština: 'fr',
	frízština: 'fy',
	galícijčina: 'gl',
	gruzínčina: 'ka',
	nemčina: 'de',
	gréčtina: 'el',
	guaraní: 'gn',
	gudžarátčina: 'gu',
	'haitská kreolčina': 'ht',
	hauština: 'ha',
	havajčina: 'haw',
	hebrejčina: 'he',
	hindčina: 'hi',
	hmongčina: 'hmn',
	maďarčina: 'hu',
	islandčina: 'is',
	igbo: 'ig',
	ilocano: 'ilo',
	indonézčina: 'id',
	írčina: 'ga',
	taliančina: 'it',
	japončina: 'ja',
	javánčina: 'jv',
	kannadčina: 'kn',
	kazaština: 'kk',
	kmérčina: 'km',
	kinyarwanda: 'rw',
	konkani: 'gom',
	kórejčina: 'ko',
	krio: 'kri',
	kurdčina: 'ku',
	'kurdčina (sorani)': 'ckb',
	kirgizština: 'ky',
	laoština: 'lo',
	latinčina: 'la',
	lotyština: 'lv',
	lingala: 'ln',
	litovčina: 'lt',
	luganda: 'lg',
	luxemburčina: 'lb',
	macedónčina: 'mk',
	maithili: 'mai',
	malgaština: 'mg',
	malajčina: 'ms',
	malajálamčina: 'ml',
	maltčina: 'mt',
	maorijčina: 'mi',
	maráthčina: 'mr',
	'meiteilon (manipurská)': 'mni-Mtei',
	mizčina: 'lus',
	mongolčina: 'mn',
	'mjanmarská (barmčina)': 'my',
	nepálčina: 'ne',
	nórčina: 'no',
	'nianja (čewa)': 'ny',
	urijčina: 'or',
	oromčina: 'om',
	paštčina: 'ps',
	perzština: 'fa',
	poľština: 'pl',
	portugalčina: 'pt',
	pandžábčina: 'pa',
	kečuánčina: 'qu',
	rumunčina: 'ro',
	ruština: 'ru',
	samojčina: 'sm',
	sanskrit: 'sa',
	'škótska gaelčina': 'gd',
	sepedi: 'nso',
	srbčina: 'sr',
	sesotho: 'st',
	šona: 'sn',
	sindčina: 'sd',
	sinhálčina: 'si',
	slovenčina: 'sk',
	slovinčina: 'sl',
	somálčina: 'so',
	španielčina: 'es',
	sundánčina: 'su',
	svahilčina: 'sw',
	švédčina: 'sv',
	'tagalčina (filipínčina)': 'tl',
	tadžičtina: 'tg',
	tamilčina: 'ta',
	tatárčina: 'tt',
	telugčina: 'te',
	thajčina: 'th',
	tigriňa: 'ti',
	tsongčina: 'ts',
	turečtina: 'tr',
	turkménčina: 'tk',
	'twi (akančina)': 'ak',
	ukrajinčina: 'uk',
	urdčina: 'ur',
	ujgurčina: 'ug',
	uzbečtina: 'uz',
	vietnamčina: 'vi',
	waleština: 'cy',
	xhoština: 'xh',
	jidiš: 'yi',
	jorubčina: 'yo',
	zuluština: 'zu'
}

const useConfigStore = defineStore('config', () => {
	const languages = ref([])
	const currencies = ref([])

	const selectedLanguage = ref(null)

	const fetchLanguages = async () => {
		try {
			const response = await apiFetch('/languages')

			languages.value = response

			selectedLanguage.value = response?.[0]?.id

			return Promise.resolve(response)
		} catch (error) {
			return Promise.reject(error)
		}
	}

	const fetchCurrencies = async () => {
		try {
			const response = await apiFetch('/currencies')

			currencies.value = response

			return Promise.resolve(response)
		} catch (error) {
			return Promise.reject(error)
		}
	}

	// Meny sa inak načítajú len pri prihlásení, po obnovení stránky by výber meny pri cene bol prázdny
	let currenciesPromise = null
	const ensureCurrencies = () => {
		if (currencies.value?.length) return Promise.resolve(currencies.value)
		currenciesPromise ??= fetchCurrencies().catch((error) => {
			currenciesPromise = null
			return Promise.reject(error)
		})
		return currenciesPromise
	}

	const getLanguageFromId = (id, property) => {
		const language = languages.value?.find((language) => language.id === id)

		if (property) {
			return language?.[property]
		}

		return language
	}

	const getDefaultLanguage = () => {
		return languages.value?.find((language) => language.default)
	}

	return {
		languages,
		currencies,
		selectedLanguage,
		fetchLanguages,
		fetchCurrencies,
		ensureCurrencies,
		getLanguageFromId,
		getDefaultLanguage,
		translationLanguages: TRANSLATION_LANGUAGES
	}
})

export default useConfigStore
